import React, { useRef, useEffect, useMemo } from 'react';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { dracula } from '@uiw/codemirror-theme-dracula';
import { StreamLanguage } from '@codemirror/language';
import { shell } from '@codemirror/legacy-modes/mode/shell';
import { EditorView, keymap } from '@codemirror/view';
import {
  indentMore,
  indentLess,
  toggleComment,
  insertTab,
} from '@codemirror/commands';
import classnames from 'classnames';
import { Icon } from '@puppet/react-components';

const shellLanguage = StreamLanguage.define(shell);

const shellKeymap = keymap.of([
  {
    key: 'Tab',
    run: (view) => {
      if (view.state.selection.ranges.some((r) => !r.empty)) {
        return indentMore(view);
      }
      return insertTab(view);
    },
    shift: indentLess,
  },
  {
    key: 'Ctrl-/',
    run: toggleComment,
  },
  {
    key: 'Mod-/',
    run: toggleComment,
  },
]);

const defaultProps = {
  active: true,
  className: '',
  error: null,
  label: null,
  readOnly: false,
};

interface Props {
  active?: boolean;
  className?: string;
  error?: string | null;
  label?: string | React.ReactElement;
  name: string;
  onChange: (v: string) => void;
  value: string;
  readOnly?: boolean;
}

const renderError = (error: Props['error']) => {
  if (!error) {
    return null;
  }

  return (
    <div className="shell-editor__error">
      <Icon type="alert" className="shell-editor__error--icon" size="small" />
      {error}
    </div>
  );
};

const ShellEditor = ({
  active,
  className,
  error,
  label,
  name,
  onChange,
  value,
  readOnly,
}: Props) => {
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  // Sets aria-labelledby on contentDOM so that RTL's getByLabelText can find it.
  // RTL 9.x only considers native form elements "labellable" via htmlFor/id,
  // but it DOES find elements with aria-labelledby. The label's id is "${name}-label".
  const contentAriaExtension = useMemo(
    () =>
      EditorView.contentAttributes.of({ 'aria-labelledby': `${name}-label` }),
    [name],
  );

  // If the user loads a page in which the editor is hidden by css, for example
  // if it is in a hidden tab, codemirror won't render properly. This effect hook
  // allows the consumer to pass a flag that will force a refresh when the editor becomes active
  useEffect(() => {
    if (editorRef.current?.view) {
      editorRef.current.view.requestMeasure();
    }
  }, [active]);

  // The `shell-editor-${name}` class is added to simplify finding the correct
  // CodeMirror instance during testing
  return (
    <div
      className={`shell-editor shell-editor-${name}`}
      data-testid={`shell-editor-${name}`}
    >
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- CM6 contentDOM is a div[role=textbox] linked via aria-labelledby, not a native form control */}
      {label && <label id={`${name}-label`}>{label}</label>}
      <CodeMirror
        ref={editorRef}
        value={value}
        className={classnames('shell-editor__editor', className, {
          'shell-editor__editor--error': error,
        })}
        theme={dracula}
        extensions={[
          shellLanguage,
          shellKeymap,
          contentAriaExtension,
          EditorView.lineWrapping,
        ]}
        readOnly={readOnly}
        basicSetup={{
          lineNumbers: true,
          tabSize: 2,
          highlightActiveLine: false,
          foldGutter: false,
          autocompletion: false,
        }}
        onChange={(newValue) => {
          onChange(newValue);
        }}
      />
      {renderError(error)}
    </div>
  );
};

ShellEditor.defaultProps = defaultProps;

export default ShellEditor;
